/**
 * StageEditorRowContainer — per-row wiring for the pipeline Stage editor row
 * (SE-6554).
 *
 * The organism still addresses every handler by `stageId`; this container binds
 * them to THIS row so the View receives zero-argument callbacks and its JSX
 * never allocates a closure in an attribute (react-perf/jsx-no-new-function-as-prop).
 *
 * It also owns the one piece of state the row now has: whether its overflow
 * menu is open. Rename, the Sensitive/Terminal flags, and Delete became rows in
 * that menu (the design puts every per-stage action behind a single trigger, as
 * `StageOptionsMenu` already does on the board). Each row keeps the testID its
 * inline control carried, so existing contracts still resolve — only the
 * interaction that reaches them changed.
 * @module features/player-kanban/components/molecules/StageEditorRow/StageEditorRowContainer
 */

import {
  ArrowDown,
  ArrowUp,
  Edit2,
  Eye,
  EyeOff,
  LogIn,
  LogOut,
  Timer,
  Trash2,
} from "lucide-react-native";
import { memo, useCallback, useMemo, useState } from "react";

import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

import type { DesktopDropdownMenuItem } from "@/components/molecules/DesktopDropdownMenu";

import StageEditorRowView from "./StageEditorRowView";

/** Props for {@link StageEditorRowContainer}. */
export interface StageEditorRowContainerProps {
  /** Stage id (drives testIDs + handler routing). */
  readonly stageId: string;
  /** Current stage name (display value when not renaming). */
  readonly name: string;
  /** The stage's glossary line ("" renders no description). */
  readonly description: string;
  /** Whether the stage is protected (sensitive — gates the reason prompt). */
  readonly isProtected: boolean;
  /** Whether the stage is terminal (a close stage). */
  readonly isTerminal: boolean;
  /** How many automation rules reference this stage. */
  readonly ruleCount: number;
  /** How many cards sit in the stage. */
  readonly cardCount: number;
  /** The protection notice, or "" when there is nothing to warn about. */
  readonly protectionNotice: string;
  /** SE-5497: the stage's configured time-in-stage target in days (null = none). */
  readonly timeTargetDays: number | null;
  /** Whether the inline rename input is active. */
  readonly isEditingName: boolean;
  /** The in-progress rename value (only meaningful while editing). */
  readonly editNameValue: string;
  /** Whether this is the first stage (disables move-up). */
  readonly isFirst: boolean;
  /** Whether this is the last stage (disables move-down). */
  readonly isLast: boolean;
  /** Whether a write for this row is in flight (disables controls). */
  readonly isBusy: boolean;
  /** Begin an inline rename. */
  readonly onStartRename: (stageId: string) => void;
  /** Rename input change. */
  readonly onChangeName: (value: string) => void;
  /** Commit the rename. */
  readonly onSaveRename: (stageId: string) => void;
  /** Abandon the rename. */
  readonly onCancelRename: () => void;
  /** Toggle the protected flag. */
  readonly onToggleProtected: (stageId: string, next: boolean) => void;
  /** Toggle the terminal flag. */
  readonly onToggleTerminal: (stageId: string, next: boolean) => void;
  /** SE-5497: commit a new time-in-stage target (null clears it). */
  readonly onChangeTarget: (stageId: string, next: number | null) => void;
  /** Move the stage up one position. */
  readonly onMoveUp: (stageId: string) => void;
  /** Move the stage down one position. */
  readonly onMoveDown: (stageId: string) => void;
  /** Request deletion of the stage. */
  readonly onDelete: (stageId: string) => void;
}

/**
 * Bind one stage row's handlers and own its overflow-menu state.
 * @param props - {@link StageEditorRowContainerProps}.
 * @returns The wired {@link StageEditorRowView}.
 */
const StageEditorRowContainer = (props: StageEditorRowContainerProps) => {
  const {
    stageId,
    name,
    isProtected,
    isTerminal,
    isBusy,
    onStartRename,
    onSaveRename,
    onToggleProtected,
    onToggleTerminal,
    onMoveUp,
    onMoveDown,
    onDelete,
  } = props;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const handleMenuToggle = useCallback(() => setIsMenuOpen(prev => !prev), []);
  const handleMenuClose = useCallback(() => setIsMenuOpen(false), []);

  /**
   * Whether the time-target editor is revealed under the row. Local because
   * only this row's menu opens it and nothing above needs to know.
   */
  const [isEditingTarget, setIsEditingTarget] = useState(false);
  const handleOpenTargetEditor = useCallback(
    () => setIsEditingTarget(true),
    []
  );
  const handleCloseTargetEditor = useCallback(
    () => setIsEditingTarget(false),
    []
  );

  const handleStartRename = useCallback(
    () => onStartRename(stageId),
    [onStartRename, stageId]
  );
  const handleSaveRename = useCallback(
    () => onSaveRename(stageId),
    [onSaveRename, stageId]
  );
  const handleToggleProtected = useCallback(
    () => onToggleProtected(stageId, !isProtected),
    [onToggleProtected, stageId, isProtected]
  );
  const handleToggleTerminal = useCallback(
    () => onToggleTerminal(stageId, !isTerminal),
    [onToggleTerminal, stageId, isTerminal]
  );
  const handleMoveUp = useCallback(
    () => onMoveUp(stageId),
    [onMoveUp, stageId]
  );
  const handleMoveDown = useCallback(
    () => onMoveDown(stageId),
    [onMoveDown, stageId]
  );
  const handleDelete = useCallback(
    () => onDelete(stageId),
    [onDelete, stageId]
  );

  const menuItems = useMemo<DesktopDropdownMenuItem[]>(
    () => [
      {
        id: "rename-stage",
        label: "Rename",
        icon: Edit2,
        onPress: handleStartRename,
        disabled: isBusy,
        testID: TEST_ID_PLAYER_KANBAN.stageEditorRenameStartFor(stageId),
      },
      /*
       * Reorder. These two rows carry the EXACT testIDs the inline chevrons
       * carried, so `kanban-stage-reorder-positions.spec.ts` and the row's own
       * unit tests still resolve the same ids — what changed is that the menu
       * has to be opened first. Disabled at the board edges, which is what
       * makes a first-row "Move up" a no-op rather than a silent reorder.
       */
      {
        id: "move-up",
        label: "Move up",
        icon: ArrowUp,
        onPress: handleMoveUp,
        disabled: isBusy || props.isFirst,
        testID: TEST_ID_PLAYER_KANBAN.stageEditorMoveUpFor(stageId),
      },
      {
        id: "move-down",
        label: "Move down",
        icon: ArrowDown,
        onPress: handleMoveDown,
        disabled: isBusy || props.isLast,
        testID: TEST_ID_PLAYER_KANBAN.stageEditorMoveDownFor(stageId),
      },
      {
        id: "set-time-target",
        label: "Set time target…",
        icon: Timer,
        onPress: handleOpenTargetEditor,
        disabled: isBusy,
        testID: TEST_ID_PLAYER_KANBAN.stageEditorTimeTargetOpenFor(stageId),
      },
      {
        id: "toggle-sensitive",
        label: isProtected ? "Remove Sensitive" : "Mark as Sensitive",
        icon: isProtected ? EyeOff : Eye,
        onPress: handleToggleProtected,
        disabled: isBusy,
        testID: TEST_ID_PLAYER_KANBAN.stageEditorProtectedToggleFor(stageId),
      },
      {
        id: "toggle-terminal",
        label: isTerminal ? "Remove Terminal" : "Mark as Terminal",
        icon: isTerminal ? LogIn : LogOut,
        onPress: handleToggleTerminal,
        disabled: isBusy,
        testID: TEST_ID_PLAYER_KANBAN.stageEditorTerminalToggleFor(stageId),
      },
      {
        id: "delete-stage",
        label: "Delete stage",
        icon: Trash2,
        onPress: handleDelete,
        destructive: true,
        disabled: isBusy,
        testID: TEST_ID_PLAYER_KANBAN.stageEditorDeleteFor(stageId),
      },
    ],
    [
      stageId,
      isProtected,
      isTerminal,
      isBusy,
      props.isFirst,
      props.isLast,
      handleStartRename,
      handleMoveUp,
      handleMoveDown,
      handleOpenTargetEditor,
      handleToggleProtected,
      handleToggleTerminal,
      handleDelete,
    ]
  );

  return (
    <StageEditorRowView
      stageId={stageId}
      name={name}
      description={props.description}
      isProtected={isProtected}
      isTerminal={isTerminal}
      isGuarded={props.ruleCount > 0}
      cardCount={props.cardCount}
      protectionNotice={props.protectionNotice}
      timeTargetDays={props.timeTargetDays}
      isEditingName={props.isEditingName}
      editNameValue={props.editNameValue}
      isEditingTarget={isEditingTarget}
      onCloseTargetEditor={handleCloseTargetEditor}
      isBusy={isBusy}
      menuItems={menuItems}
      isMenuOpen={isMenuOpen}
      onMenuToggle={handleMenuToggle}
      onMenuClose={handleMenuClose}
      onChangeName={props.onChangeName}
      onSaveRename={handleSaveRename}
      onCancelRename={props.onCancelRename}
      onChangeTarget={props.onChangeTarget}
    />
  );
};

StageEditorRowContainer.displayName = "StageEditorRowContainer";

export default memo(StageEditorRowContainer);
